import React, { useRef, useState } from "react";
import BackButton from "../components/BackButton";
import "./CozeRunner.css";
import { apiFetch } from "../api";

export default function CopyAssistant() {
  const DEBUG = false;
  const BOT_ID = "7603771015692746793";

  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [chunks, setChunks] = useState([]);
  const [streamRaw, setStreamRaw] = useState("");

  const abortRef = useRef(null);

  const renderTextWithLinks = (text) => {
    const urlRegex = /https?:\/\/[^\s<>\"|]+/g;
    const trimPunct = /[)\]>,.;!?\"']+$/;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      let url = match[0];
      const raw = url;
      url = url.replace(trimPunct, "");
      const start = match.index;
      if (start > lastIndex) parts.push(text.slice(lastIndex, start));
      parts.push(
        <a key={`${url}-${start}`} href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      );
      lastIndex = start + raw.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts;
  };

  const renderResult = (text) =>
    text.split(/\r?\n/).map((line, i) => (
      <span key={`ln-${i}`}>
        {renderTextWithLinks(line)}
        <br />
      </span>
    ));

  const makeIdemKey = () => {
    try { return crypto.randomUUID(); } catch {}
    return (Date.now().toString(36) + Math.random().toString(36).slice(2)).slice(0, 64);
  };

  async function handleRunStream(e) {
    e.preventDefault();
    if (loading) return;
    setError("");
    setChunks([]);
    setStreamRaw("");

    if (!prompt.trim()) {
      setError("请输入产品/场景或需求");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setError("请先登录后再调用");
      return;
    }

    setLoading(true);
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const r = await apiFetch("/api/coze/hook3s/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
          "Authorization": `Bearer ${token}`,
          "Idempotency-Key": makeIdemKey(),
        },
        body: { prompt, bot_id: BOT_ID },
        raw: true,
        signal: ac.signal,
      });

      if (r.status === 401) {
        setError("未登录或登录已过期，请重新登录");
        setLoading(false);
        return;
      }
      if (!r.ok) {
        const maybe = await r.text().catch(() => "");
        throw new Error(maybe || r.statusText);
      }

      const reader = r.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error("当前环境不支持可读流");

      let buffer = "";

      const flushFrame = async (frame) => {
        if (!frame.trim()) return;
        if (DEBUG) setStreamRaw((s) => s + frame + "\n\n");

        const lines = frame.split(/\r?\n/);
        let eventName = "message";
        const dataLines = [];

        for (const ln of lines) {
          if (ln.startsWith("event:")) eventName = ln.slice(6).trim();
          else if (ln.startsWith("data:")) dataLines.push(ln.slice(5).trim());
        }

        const dataStr = dataLines.join("\n");
        let dataObj = {};
        try { dataObj = JSON.parse(dataStr); } catch { dataObj = { text: dataStr }; }

        if (eventName === "error") {
          setError(dataObj?.message || "上游错误");
          return;
        }
        if (eventName === "done") return;

        const delta = dataObj?.delta || dataObj?.content || dataObj?.text || "";
        if (delta) setChunks((arr) => [...arr, delta]);
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) { if (buffer) await flushFrame(buffer); break; }
        buffer += decoder.decode(value, { stream: true });

        const frames = buffer.split(/\r?\n\r?\n/);
        buffer = frames.pop() || "";
        for (const fr of frames) await flushFrame(fr);
      }
    } catch (err) {
      if (err && err.name !== "AbortError") setError(err.message || "流式请求失败");
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }

  function handleCancel() {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
  }

  return (
    <main className="coze-wrap">
      <div className="coze-header">
        <BackButton />
        <div>
          <h1 className="coze-title">开头3s钩子智能体</h1>
          <p className="coze-subtitle">输入产品/场景，输出3秒抓人开头与钩子句。</p>
        </div>
      </div>

      <form className="coze-form" onSubmit={handleRunStream}>
        <textarea
          className="coze-input"
          required
          placeholder="例如：英语机构短视频引流，帮我写3秒钩子开头"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
        />
        <div className="coze-actions">
          <button className="coze-btn" type="submit" disabled={loading}>
            {loading ? "运行中…" : "开始生成"}
          </button>
          <button className="coze-btn" type="button" onClick={handleCancel} disabled={!abortRef.current}>
            取消
          </button>
        </div>
      </form>

      {error && <div className="coze-error">错误：{error}</div>}

      <section className="coze-card" style={{ marginTop: 12 }}>
        <h3>生成结果</h3>
        {loading && chunks.length === 0 && (
          <div className="coze-result" style={{ opacity: 0.8 }}>
            正在生成中，请稍候…
          </div>
        )}
        {!loading && chunks.length === 0 && (
          <div className="coze-result" style={{ opacity: 0.6 }}>
            暂无内容
          </div>
        )}
        {chunks.length > 0 && (
          <div className="coze-result">
            {renderResult(chunks.join(""))}
          </div>
        )}
      </section>

      {DEBUG && streamRaw && (
        <section className="coze-card">
          <h3>原始事件（调试）</h3>
          <pre className="coze-result" style={{ maxHeight: 260, overflow: "auto" }}>
            {streamRaw}
          </pre>
        </section>
      )}
    </main>
  );
}
